import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Typography,
  Checkbox,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import CategoryIcon from '@mui/icons-material/Category';
import salesService from '../../../../../services/salesService';
import { useTranslation } from 'react-i18next';

// Interfaces
interface InventoryItem {
  id: string;
  name: string;
  barcode?: string;
  stockCode?: string;
  quantity?: number;
  unit?: string;
  salePrice?: number;
  imageUrl?: string | null; // ✅ YENİ: Resim URL'i ekle
  warehouseId?: string | null;
  categoryId?: string | null;
}

interface Warehouse {
  id: string;
  name: string;
  isDefault?: boolean;
}

interface InventoryCategory {
  id: string;
  name: string;
  parentId?: string | null;
  level?: number;
}

interface QuickSaleProductSelectionModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null; // ✅ YENİ: companyId prop
  allWarehouses: Warehouse[];
  allCategories: InventoryCategory[];
  initialWarehouseId: string | null;
  initialCategoryId: string | null;
  onSelectProducts: (selectedProductIds: string[], selectedProductsData: InventoryItem[]) => void; // ✅ YENİ: ürün bilgilerini de döndür
  alreadySelectedProductIds: string[];
  existingProductsDetails?: { [productId: string]: any }; // ✅ YENİ: Önceden seçili ürünlerin detayları
}

const QuickSaleProductSelectionModal: React.FC<QuickSaleProductSelectionModalProps> = ({
  open,
  onClose,
  companyId, // ✅ YENİ
  allWarehouses,
  allCategories,
  initialWarehouseId,
  initialCategoryId,
  onSelectProducts,
  alreadySelectedProductIds,
  existingProductsDetails = {}, // ✅ YENİ: Önceden seçili ürünlerin detayları
}) => {
  const { t } = useTranslation('salesSystem');
  const [searchTerm, setSearchTerm] = useState('');
  // PERFORMANS: API'ye giden arama metni — 400 ms debounce, min. 2 karakter (Enter ile hemen)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const requestSeqRef = useRef(0);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentlySelectedIds, setCurrentlySelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Set initial filters and selection when modal opens
  useEffect(() => {
    if (open) {
      // ✅ DEĞİŞİKLİK: Artık otomatik depo/kategori seçimi yapılmıyor - kullanıcı kendisi seçecek
      setSelectedWarehouseId(null); // initialWarehouseId yerine null
      setSelectedCategoryId(null); // initialCategoryId yerine null
      setCurrentlySelectedIds([...alreadySelectedProductIds]);
      setSearchTerm('');
      setDebouncedSearch('');
      setPage(0);
    }
  }, [open, alreadySelectedProductIds]);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed.length === 1) return undefined; // tek karakterle tüm ürünleri tarama
    const timer = setTimeout(() => setDebouncedSearch(trimmed), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]); // initialWarehouseId ve initialCategoryId dependency'den kaldırıldı

  // ✅ YENİ: Server-side pagination ile ürünleri yükle
  useEffect(() => {
    if (!open || !companyId) return;

    const loadProducts = async () => {
      const seq = ++requestSeqRef.current;
      setLoading(true);
      try {
        console.log('📦 [PRODUCT MODAL] Ürünler yükleniyor...', {
          page: page + 1,
          rowsPerPage,
          searchTerm: debouncedSearch,
          warehouseId: selectedWarehouseId,
          categoryId: selectedCategoryId,
          companyId: companyId
        });
        
        // ✅ SALES API URL'ini logla
        console.log('🌐 [PRODUCT MODAL] SALES API URL:', process.env.REACT_APP_SALESAPI_URL || 'http://localhost:5238');
        console.log('🌐 [PRODUCT MODAL] Tam Endpoint:', `${process.env.REACT_APP_SALESAPI_URL || 'http://localhost:5238'}/api/QuickSaleScreen/products/paged`);

        const response = await salesService.getProductsPaged(
          page + 1,
          rowsPerPage,
          debouncedSearch || undefined,
          selectedWarehouseId || undefined,
          selectedCategoryId || undefined
        );

        if (seq !== requestSeqRef.current) return; // daha yeni istek var
        console.log('✅ [PRODUCT MODAL] API response:', response);
        console.log('✅ [PRODUCT MODAL] Response type:', typeof response);
        console.log('✅ [PRODUCT MODAL] Response keys:', Object.keys(response || {}));

        // API'den gelen items dizisini parse et (hem büyük hem küçük harf desteği)
        const itemsArray = response.items || response.Items || [];
        const totalCount = response.totalCount || response.TotalCount || 0;

        console.log(`📊 [PRODUCT MODAL] Raw items count: ${itemsArray.length}, Total: ${totalCount}`);

        const parsedItems: InventoryItem[] = itemsArray.map((item: any) => ({
          id: item.id?.toString() || item.Id?.toString() || '',
          name: item.name || item.Name || '',
          barcode: item.barcode || item.Barcode || '',
          stockCode: item.stockCode || item.StockCode || '',
          quantity: item.totalQuantity || item.TotalQuantity || 0,
          unit: item.unit || item.Unit || '',
          salePrice: item.salePrice || item.SalePrice || 0,
          imageUrl: item.imageUrl || item.ImageUrl || null, // ✅ YENİ: Resim URL'i ekle
          warehouseId: null, // ⚠️ Yeni sistemde WarehouseStocks array'i var
          categoryId: item.categoryId || item.CategoryId || null,
        }));

        setItems(parsedItems);
        setTotalCount(totalCount);

        console.log(`✅ [PRODUCT MODAL] ${parsedItems.length} ürün yüklendi (Toplam: ${totalCount})`);
      } catch (error: any) {
        console.error('❌ [PRODUCT MODAL] Ürün yükleme hatası:', error);
        console.error('❌ [PRODUCT MODAL] Hata detayı:', {
          message: error?.message,
          response: error?.response,
          responseData: error?.response?.data,
          status: error?.response?.status,
          statusText: error?.response?.statusText
        });
        if (seq !== requestSeqRef.current) return;
        setItems([]);
        setTotalCount(0);
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    };

    loadProducts();
  }, [open, companyId, page, rowsPerPage, debouncedSearch, selectedWarehouseId, selectedCategoryId]);

  // Pagination handlers
  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Selection handlers
  const handleToggleSelect = (itemId: string) => {
    setCurrentlySelectedIds(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = items.map((n) => n.id);
      // Merge with existing selections that might be on other pages
      const combined = Array.from(new Set([...currentlySelectedIds, ...newSelecteds]));
      setCurrentlySelectedIds(combined);
      return;
    }
    // Deselect only the items on the current page
    const pageItemIds = items.map(item => item.id);
    setCurrentlySelectedIds(prev => prev.filter(id => !pageItemIds.includes(id)));
  };

  // Save selection
  const handleSaveSelection = () => {
    // Şu anki sayfadaki seçili ürünlerin tam bilgilerini al
    const currentPageSelectedProducts = items.filter(item => currentlySelectedIds.includes(item.id));
    
    // Tüm seçili ürünlerin detaylarını topla
    const allSelectedProductsMap = new Map<string, InventoryItem>();
    
    // Önce mevcut detayları ekle (önceden seçilmiş ürünler için)
    currentlySelectedIds.forEach(id => {
      if (existingProductsDetails[id]) {
        const existing = existingProductsDetails[id];
        allSelectedProductsMap.set(id, {
          id: existing.id?.toString() || id,
          name: existing.name || '',
          barcode: existing.barcode || '',
          stockCode: existing.stockCode || '',
          quantity: existing.quantity || 0,
          unit: existing.unit || '',
          salePrice: existing.salePrice || 0,
          imageUrl: existing.imageUrl || null, // ✅ Önceki resimleri koru
          warehouseId: existing.warehouseId || null,
          categoryId: existing.categoryId || null,
        });
      }
    });
    
    // Sonra şu anki sayfadaki yeni seçili ürünleri ekle/güncelle
    currentPageSelectedProducts.forEach(product => {
      allSelectedProductsMap.set(product.id, product);
    });
    
    const allSelectedProducts = Array.from(allSelectedProductsMap.values());
    
    console.log('📦 [PRODUCT MODAL] Seçilen ürünler (imageUrl ile):', allSelectedProducts.map(p => ({ id: p.id, name: p.name, imageUrl: p.imageUrl })));
    onSelectProducts(currentlySelectedIds, allSelectedProducts);
    onClose();
  };

  const numSelectedOnPage = items.filter(item => currentlySelectedIds.includes(item.id)).length;
  const rowCountOnPage = items.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('quickSaleProductSelectionModal.title')}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('quickSaleProductSelectionModal.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0); // Reset page on search
              }}
              onKeyDown={(e) => {
                // Enter / barkod okuyucu: beklemeden ara
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setDebouncedSearch(searchTerm.trim());
                  setPage(0);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="warehouse-filter-label">{t('quickSaleProductSelectionModal.filters.warehouse')}</InputLabel>
              <Select
                labelId="warehouse-filter-label"
                value={selectedWarehouseId || 'all'}
                onChange={(e: SelectChangeEvent<string>) => {
                  const value = e.target.value === 'all' ? null : e.target.value;
                  setSelectedWarehouseId(value);
                  setPage(0); // Reset page on filter change
                }}
                label={t('quickSaleProductSelectionModal.filters.warehouse')}
                startAdornment={
                  <InputAdornment position="start">
                    <WarehouseIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">{t('quickSaleProductSelectionModal.filters.allWarehouses')}</MenuItem>
                {allWarehouses.map(warehouse => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="category-filter-label">{t('quickSaleProductSelectionModal.filters.category')}</InputLabel>
              <Select
                labelId="category-filter-label"
                value={selectedCategoryId || 'all'}
                onChange={(e: SelectChangeEvent<string>) => {
                  const value = e.target.value === 'all' ? null : e.target.value;
                  setSelectedCategoryId(value);
                  setPage(0); // Reset page on filter change
                }}
                label={t('quickSaleProductSelectionModal.filters.category')}
                startAdornment={
                  <InputAdornment position="start">
                    <CategoryIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">{t('quickSaleProductSelectionModal.filters.allCategories')}</MenuItem>
                {allCategories.sort((a, b) => a.name.localeCompare(b.name)).map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Product Table */}
        <Box sx={{ position: 'relative', minHeight: 400 }}>
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
            }}>
              <CircularProgress />
            </Box>
          )}
          
          <TableContainer sx={{ maxHeight: 450 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={numSelectedOnPage > 0 && numSelectedOnPage < rowCountOnPage}
                      checked={rowCountOnPage > 0 && numSelectedOnPage === rowCountOnPage}
                      onChange={handleSelectAllClick}
                      inputProps={{
                        'aria-label': 'select all items on this page',
                      }}
                    />
                  </TableCell>
                  <TableCell>{t('quickSaleProductSelectionModal.table.productName')}</TableCell>
                  <TableCell>{t('quickSaleProductSelectionModal.table.stockCode')}</TableCell>
                  <TableCell>{t('quickSaleProductSelectionModal.table.barcode')}</TableCell>
                  <TableCell>{t('quickSaleProductSelectionModal.table.quantity')}</TableCell>
                  <TableCell>{t('quickSaleProductSelectionModal.table.price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('quickSaleProductSelectionModal.empty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    const isSelected = currentlySelectedIds.includes(item.id);
                    const labelId = `product-checkbox-${item.id}`;

                    return (
                      <TableRow
                        hover
                        onClick={() => handleToggleSelect(item.id)}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={-1}
                        key={item.id}
                        selected={isSelected}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isSelected}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                          />
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row">
                          {item.name}
                        </TableCell>
                        <TableCell>{item.stockCode || '-'}</TableCell>
                        <TableCell>{item.barcode || '-'}</TableCell>
                        <TableCell>{item.quantity || 0} {item.unit || ''}</TableCell>
                        <TableCell>{item.salePrice?.toFixed(2) || '0.00'} TL</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage={t('quickSaleProductSelectionModal.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('quickSaleProductSelectionModal.pagination.displayedRows', { from, to, count })}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('quickSaleProductSelectionModal.actions.cancel')}</Button>
        <Button onClick={handleSaveSelection} variant="contained">
          {t('quickSaleProductSelectionModal.actions.addSelected', { count: currentlySelectedIds.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickSaleProductSelectionModal; 